/**
 * Industry — jobs del char con valuación Jita + margin real.
 *
 * Layout (de arriba a abajo):
 *
 *   1. Hero "Profit potential" — único número grande, signo + color.
 *      Sub: split manuf/react + ratio rentables + avg margin %.
 *      Es LA respuesta a "¿estoy ganando ISK con esto?".
 *
 *   2. Strip secundario — 4 chips chicos: activos / con precio / sin precio /
 *      terminan pronto. Contexto sin competir con el hero.
 *
 *   3. Filtros sticky — status chips + sort + group + materials source.
 *      Persistencia localStorage para no resetear en cada navegación.
 *
 *   4. Lista de jobs, agrupable por actividad:
 *      - L1: Blueprint name (bold)
 *      - L2: Progress bar
 *      - L3: ETA + status + valuation
 *      - L4: Footer con materials source + hint
 *      - Click expand: materials breakdown
 *
 *   5. MarginHistoryChart + SuggestionsCard — al final, con subtítulo
 *      explicando qué son.
 *
 * Data flow:
 *   - useQuery ['industry-valuation', includeCompleted, force, source]
 *   - useNow(1000) para countdown live-ticking — estable en el page
 *     (no en cada row) para evitar React #310.
 *   - Filtros son client-side (no re-fetch).
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { useNow } from '../hooks/useNow'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { MarginHistoryChart } from '../components/MarginHistoryChart'
import { SuggestionsCard } from '../components/SuggestionsCard'
import { IndustryJobRow } from '../components/IndustryJobRow'
import {
  IndustryFilters,
  usePersistedFilters,
  type StatusFilter,
} from '../components/IndustryFilters'
import { isDone, type ValuedJob } from '../lib/industry'
import { Pill, StatTile, EmptyState } from '../components/ui'

export function Industry() {
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [useEsiMaterials, setUseEsiMaterials] = useState(false)
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  // Hook estable en cada render. Antes vivía dentro de JobRow, donde su
  // conteo variaba según displayed.length (0 vs N) → React #310.
  const now = useNow(1000)
  const materialsSource: 'fuzzwork' | 'esi' = useEsiMaterials ? 'esi' : 'fuzzwork'
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['industry-valuation', includeCompleted, forceRefetchKey, materialsSource],
    queryFn: () => api.industryValuation(includeCompleted, force, materialsSource),
  })

  const allJobs: ValuedJob[] = data?.jobs ?? []
  const summary = data?.summary

  // Persisted filters
  const {
    status: statusFilter,
    sort: sortKey,
    groupByActivity,
    setStatus: setStatusFilter,
    setSort: setSortKey,
    setGroupByActivity,
  } = usePersistedFilters()

  // 1) Filter by status. Done = delivered/cancelled/reverted.
  const filtered = useMemo(() => {
    const matchStatus = (j: ValuedJob): boolean => {
      const done = isDone(j.status)
      switch (statusFilter) {
        case 'all':
          return true
        case 'active':
          return j.status === 'active'
        case 'ready':
          return j.status === 'ready'
        case 'paused':
          return j.status === 'paused'
        case 'done':
          return done
      }
    }
    return allJobs.filter(matchStatus)
  }, [allJobs, statusFilter])

  // 2) Sort.
  const sorted = useMemo(() => {
    const arr = [...filtered]
    switch (sortKey) {
      case 'eta':
        arr.sort((a, b) => new Date(a.end_date).getTime() - new Date(b.end_date).getTime())
        break
      case 'margin_pct':
        arr.sort((a, b) => (b.margin_pct ?? -Infinity) - (a.margin_pct ?? -Infinity))
        break
      case 'margin_h':
        arr.sort(
          (a, b) => (b.margin_per_hour ?? -Infinity) - (a.margin_per_hour ?? -Infinity),
        )
        break
      case 'value':
        arr.sort(
          (a, b) =>
            (b.margin_total ?? b.expected_total_value ?? -Infinity) -
            (a.margin_total ?? a.expected_total_value ?? -Infinity),
        )
        break
    }
    return arr
  }, [filtered, sortKey])

  // 3) Group by activity (optional). Sections render in canonical order
  //    Manufacturing → Reactions → Research (other activities follow).
  const grouped = useMemo(() => {
    if (!groupByActivity) return null
    const buckets = new Map<string, ValuedJob[]>()
    for (const j of sorted) {
      const key = j.activity_name
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key)!.push(j)
    }
    // Canonical order: known activities first.
    const order = ['Manufacturing', 'Reactions', 'Research', 'Copying', 'Invention', 'ME Research', 'TE Research']
    return Array.from(buckets.entries()).sort((a, b) => {
      const ai = order.indexOf(a[0])
      const bi = order.indexOf(b[0])
      if (ai === -1 && bi === -1) return a[0].localeCompare(b[0])
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  }, [sorted, groupByActivity])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Industry</h1>
          <p className="text-eve-muted text-sm">
            Tus jobs de manufactura/research con valuación Jita + margin real.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
          <button
            onClick={() => setIncludeCompleted((v) => !v)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              includeCompleted
                ? 'bg-eve-accent/10 border-eve-accent text-eve-accent'
                : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
            }`}
            title="Incluir jobs ya entregados/cancelled/reverted en la lista. El filtro 'Completados' también lo hace, pero este switch trae TODOS los completados al cache."
          >
            {includeCompleted ? 'Cache: todo' : 'Cache: solo activos'}
          </button>
        </div>
      </div>

      {/* 1) Hero — Profit potential */}
      <ProfitHero summary={summary} isLoading={isLoading} />

      {/* 2) Strip secundario */}
      <ContextStrip summary={summary} jobs={sorted} />

      {/* 3) Filtros sticky */}
      <IndustryFilters
        status={statusFilter}
        sort={sortKey}
        groupByActivity={groupByActivity}
        useEsiMaterials={useEsiMaterials}
        onStatusChange={setStatusFilter}
        onSortChange={setSortKey}
        onGroupChange={setGroupByActivity}
        onSourceChange={setUseEsiMaterials}
        totalCount={allJobs.filter((j) => (includeCompleted ? true : !isDone(j.status))).length}
        filteredCount={sorted.length}
      />

      {/* 4) Lista */}
      <div className="card">
        {isLoading ? (
          <JobsListSkeleton />
        ) : sorted.length === 0 ? (
          <IndustryEmptyState statusFilter={statusFilter} />
        ) : groupByActivity && grouped ? (
          <div className="space-y-5">
            {grouped.map(([activityName, jobs]) => (
              <section key={activityName}>
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-xs uppercase tracking-wider text-eve-muted font-semibold">
                    {activityName}
                  </h3>
                  <span className="text-[10px] text-eve-muted-2 font-mono">
                    {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
                  </span>
                </div>
                <div className="space-y-2">
                  {jobs.map((j) => (
                    <IndustryJobRow key={j.job_id} job={j} now={now} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map((j) => (
              <IndustryJobRow key={j.job_id} job={j} now={now} />
            ))}
          </div>
        )}
      </div>

      {/* 5) Charts & suggestions — al final, con subtítulo */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-lg font-semibold text-eve-text">
            Tendencia de margen
          </h2>
          <span className="text-[10px] text-eve-muted-2 italic">
            cómo viene tu margin día a día
          </span>
        </div>
        <MarginHistoryChart />
      </section>

      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-lg font-semibold text-eve-text">
            Sugerencias para vos
          </h2>
          <span className="text-[10px] text-eve-muted-2 italic">
            manufactura rentable según tu skill + location
          </span>
        </div>
        <SuggestionsCard />
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Hero "Profit potential" — el número más importante de la página.
 *
 * Layout:
 *   - Label "Profit potential" chico, arriba
 *   - Número grande, color según signo (verde/rojo/neutro)
 *   - Sub con split manuf/react + ratio
 *
 * Si no hay jobs marginables (research-only, o todo "sin precio"), muestra
 * el número total como neutro y un mensaje explicando.
 */
function ProfitHero({
  summary,
  isLoading,
}: {
  summary:
    | {
        total_margin: number
        marginable_jobs: number
        profitable_jobs: number
        avg_margin_pct: number
        manufacturing_margin: number
        reactions_margin: number
      }
    | undefined
    | null
  isLoading: boolean
}) {
  if (isLoading && !summary) {
    return (
      <div className="card border-eve-accent/30">
        <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
          Profit potential
        </div>
        <div className="font-display text-4xl sm:text-5xl font-bold text-eve-muted mt-2 animate-pulse">
          …
        </div>
      </div>
    )
  }
  if (!summary) return null

  const margin = summary.total_margin
  const positive = margin > 0
  const negative = margin < 0
  const colorClass = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : 'text-eve-text'

  return (
    <div className="card border-eve-accent/30 bg-gradient-to-br from-eve-panel to-eve-bg">
      <div className="flex items-baseline gap-2 text-eve-muted text-[10px] uppercase tracking-widest font-medium">
        <span>Profit potential</span>
        <span className="text-eve-muted-2 normal-case tracking-normal italic">
          {summary.marginable_jobs === 0
            ? '(sin jobs manufacturables activos)'
            : `(${summary.marginable_jobs} job${summary.marginable_jobs === 1 ? '' : 's'} con margin)`}
        </span>
      </div>
      <div className={`font-display text-4xl sm:text-5xl font-bold mt-2 ${colorClass}`}>
        {positive ? '+' : ''}
        {formatISK(margin)}
        <span className="text-xl ml-2 font-mono text-eve-muted">ISK</span>
      </div>
      <div className="text-sm text-eve-muted mt-2 font-mono">
        {summary.profitable_jobs}/{summary.marginable_jobs} rentables · avg{' '}
        <span
          className={
            summary.avg_margin_pct > 0
              ? 'text-eve-success'
              : summary.avg_margin_pct < 0
                ? 'text-eve-danger'
                : ''
          }
        >
          {summary.avg_margin_pct.toFixed(1)}%
        </span>{' '}
        margin
        {summary.manufacturing_margin !== 0 && (
          <>
            {' · '}
            <span className="text-eve-muted-2">manuf </span>
            <span
              className={
                summary.manufacturing_margin > 0
                  ? 'text-eve-success'
                  : 'text-eve-danger'
              }
            >
              {formatISKShort(summary.manufacturing_margin)}
            </span>
          </>
        )}
        {summary.reactions_margin !== 0 && (
          <>
            {' · '}
            <span className="text-eve-muted-2">react </span>
            <span
              className={
                summary.reactions_margin > 0
                  ? 'text-eve-success'
                  : 'text-eve-danger'
              }
            >
              {formatISKShort(summary.reactions_margin)}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Strip secundario — 4 chips con contexto: cuántos jobs activos hay, cuántos
 * valuados, cuántos sin precio, cuántos terminan en las próximas 24h.
 *
 * Chips sin fondo (border + texto) — más sutiles que el hero, no compiten.
 */
function ContextStrip({
  summary,
  jobs,
}: {
  summary: { valued_count: number; unpriced_count: number; total_active_count: number } | undefined | null
  jobs: ValuedJob[]
}) {
  if (!summary) return null

  const activeCount = summary.total_active_count
  const pricedCount = summary.valued_count
  const unpricedCount = summary.unpriced_count
  // Terminan en < 24h: jobs con status active/ready cuyo end_date - now < 24h.
  const now = Date.now()
  const endingSoon = jobs.filter((j) => {
    if (j.status !== 'active' && j.status !== 'ready') return false
    const end = new Date(j.end_date).getTime()
    return end - now < 24 * 3600_000 && end > now
  }).length

  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <ContextPill label="activos" value={activeCount} tone="gold" />
      <span className="text-eve-muted-2">·</span>
      <ContextPill label="con precio" value={pricedCount} tone="success" />
      <span className="text-eve-muted-2">·</span>
      <ContextPill
        label="sin precio"
        value={unpricedCount}
        tone={unpricedCount > 0 ? 'warn' : 'muted'}
      />
      <span className="text-eve-muted-2">·</span>
      <ContextPill
        label="terminan < 24h"
        value={endingSoon}
        tone={endingSoon > 0 ? 'gold' : 'muted'}
      />
    </div>
  )
}

/** Pill contextual para el strip secundario. Combina un número grande (mono)
 *  con un label uppercase chico al lado. Variante del Pill del ui/ pero con
 *  tamaño custom para el contexto secundario del page. */
function ContextPill({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'gold' | 'success' | 'warn' | 'muted'
}) {
  const toneClass = {
    gold: 'text-eve-accent border-eve-accent/40',
    success: 'text-eve-success border-eve-success/40',
    warn: 'text-eve-warn border-eve-warn/40',
    muted: 'text-eve-muted border-eve-border',
  }[tone]
  return (
    <Pill
      tone={tone}
      size="md"
      className={`!bg-eve-panel/30 !rounded-full px-2.5 py-1 ${toneClass}`}
    >
      <span className="font-mono font-semibold tabular-nums normal-case tracking-normal">
        {value}
      </span>
      <span className="text-[10px] tracking-wider">{label}</span>
    </Pill>
  )
}

/**
 * Skeleton del list de jobs. Mimic la estructura visual de <IndustryJobRow>
 * para que la transición loaded → real data no haga "jump" — los widths
 * aproximados matchean una row típica (blueprint name + status badge +
 * progress bar + margin number).
 */
function JobsListSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="border border-eve-border rounded-md bg-eve-panel/30 p-3 space-y-2"
        >
          <div className="h-4 w-2/5 bg-eve-border/40 rounded animate-pulse" />
          <div className="h-1.5 bg-eve-border/40 rounded animate-pulse" />
          <div className="flex justify-between">
            <div className="h-3 w-24 bg-eve-border/40 rounded animate-pulse" />
            <div className="h-3 w-32 bg-eve-border/40 rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Empty state contextual al filtro elegido. Distintos mensajes para que
 * el user sepa qué pasó (sin jobs vs filtro demasiado restrictivo).
 */
function IndustryEmptyState({ statusFilter }: { statusFilter: StatusFilter }) {
  const messages: Record<StatusFilter, { title: string; sub: string }> = {
    all: {
      title: 'Sin jobs',
      sub: 'Arrancá manufactura desde el cliente de EVE — aparecerán acá con valuación Jita automática.',
    },
    active: {
      title: 'Sin jobs activos',
      sub: 'Todos tus jobs están pausados, listos o ya completados. Cambiá el filtro o iniciá uno nuevo.',
    },
    ready: {
      title: 'Nada listo para entregar',
      sub: 'Los jobs en estado "ready" aparecen acá cuando terminan de manufacturarse.',
    },
    paused: {
      title: 'Sin jobs en pausa',
      sub: 'Cuando pauses un job desde el cliente aparecerá acá.',
    },
    done: {
      title: 'Sin jobs completados',
      sub: 'Activa el switch "Cache: todo" arriba si querés que carguemos los completados también.',
    },
  }
  const m = messages[statusFilter]
  return (
    <EmptyState title={m.title} description={m.sub} variant="card" />
  )
}
